import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';

import { LISTING_STATE_DRAFT } from '../../util/types';
import { ensureListing } from '../../util/data';
import { findOptionsForSelectFilter } from '../../util/search';
import { EditListingFeaturesForm } from '../../forms';
import { ListingLink } from '../../components';
import config from '../../config';

import css from './EditListingFeaturesPanel.css';

const getUniversitiesString = data => data.map(item => item.university).join();
const getDisciplinaryString = data => data.join();
const getCategoryString = data => data.join();

const getDegreeArray = data => data.map(item => item.degree);
const getLanguagesArray = data => data.map(item => item.language);

const EditListingFeaturesPanel = props => {
  const {
    rootClassName,
    className,
    listing,
    disabled,
    ready,
    onSubmit,
    onChange,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const { publicData, description } = currentListing.attributes;

  const isPublished = currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
  const panelTitle = isPublished ? (
    <FormattedMessage
      id="EditListingFeaturesPanel.title"
      values={{
        listingTitle: (
          <ListingLink listing={listing}>
            <FormattedMessage id="EditListingFeaturesPanel.listingTitle" />
          </ListingLink>
        ),
      }}
    />
  ) : (
    <FormattedMessage id="EditListingFeaturesPanel.createListingTitle" />
  );

  const category = publicData && publicData.category;
  const language = (publicData && publicData.language) || [{ language: null }];
  const disciplinary = publicData && publicData.disciplinary;
  const keywords = publicData && publicData.keywords;
  const additionalInfo = publicData && publicData.additionalInfo;
  const uniDegree = publicData.uniDegree || [{ degree: null, university: null }];

  const initialValues = {
    keywords,
    category,
    language,
    disciplinary,
    uniDegree,
    additionalInfo,
    description,
  };

  const categoryOptions = findOptionsForSelectFilter('category', config.custom.filters);
  const degreeOptions = findOptionsForSelectFilter('degree', config.custom.filters);
  const languageOptions = findOptionsForSelectFilter('language', config.custom.filters);
  const disciplinaryOptions = findOptionsForSelectFilter('disciplinary', config.custom.filters);

  return (
    <div className={classes}>
      <h1 className={css.title}>{panelTitle}</h1>
      <EditListingFeaturesForm
        className={css.form}
        initialValues={initialValues}
        onSubmit={values => {
          const {
            category = [],
            language = [{ language: null }],
            disciplinary = [],
            description,
            keywords,
            additionalInfo,
            uniDegree = [{ degree: null, university: null }],
          } = values;

          const updatedValues = {
            description,
            publicData: {
              category,
              language,
              disciplinary,
              keywords,
              uniDegree,
              additionalInfo,
              universities: getUniversitiesString(uniDegree),
              languages: getLanguagesArray(language),
              degree: getDegreeArray(uniDegree),
              disciplinaries: getDisciplinaryString(disciplinary),
              categories: getCategoryString(category),
            },
          };
          onSubmit(updatedValues);
        }}
        degreeOptions={degreeOptions}
        languageOptions={languageOptions}
        disciplinaryOptions={disciplinaryOptions}
        onChange={onChange}
        saveActionMsg={submitButtonText}
        disabled={disabled}
        ready={ready}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        fetchErrors={errors}
        categoryOptions={categoryOptions}
      />
    </div>
  );
};

EditListingFeaturesPanel.defaultProps = {
  rootClassName: null,
  className: null,
  listing: null,
};

const { bool, func, object, string } = PropTypes;

EditListingFeaturesPanel.propTypes = {
  rootClassName: string,
  className: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingFeaturesPanel;
